/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  convertLicenseType,
  StatusCode,
} from '../../../../../../api/enumerations';
import { listAnnouncements } from '../../../../../../api/theHive/announcement';
import { AnnouncementData } from '../../../../../../api/theHive/announcement/types';
import {
  getNotice,
  getNoticesWorkOrders,
} from '../../../../../../api/theHive/notices';
import {
  AcceptanceDocData,
  NoticeData,
  NoticesWorkOrdersData,
} from '../../../../../../api/theHive/notices/types';
import { IOSSwitch } from '../../../../../../components/UI/Button';
import { Constants } from '../../../../../../constants/hiveNotices';
import { IconVisibilityMS } from '../../../../../../constants/icons';
import { GlobalContext } from '../../../../../../context/global';
import { formatDate } from '../../../../../../helpers';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import { DocsSection } from '../../components/AcceptanceDocsSection';
import AnnouncementTable from '../../components/AnnouncementTable';
import { NoticeAttachments } from '../../components/Attachments';
import { CoverageAreaSection } from '../../components/CoverageAreaSection';
import { DocumentsModal } from '../../components/DocumentsModal';
import { LinksModal } from '../../components/LinksModal';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { NewAnnouncementModal } from '../../components/NewAnnouncementModal';
import { PresentationVideoSection } from '../../components/PresentationVideoSection';
import { TypologyGroupSection } from '../../components/TypologyGroupSection';
import WorkOrderTable from '../../components/WorkOrderTable';
import { useAcceptanceDocs } from '../../hooks/acceptanceDocs';
import { useFetchCoverageOperations } from '../../hooks/coverage';
import { useTypologyGroups } from '../../hooks/typology';
import { NoticeVisualizationLabel } from '../../styles';
import {
  NoticeTitle,
  NoticeContainer,
  OutlinedButton,
  InputTitle,
  StyledInput,
  TableSection,
  MoreTypography,
} from '../styles';

export function NoticeDetailsConfiguration(): JSX.Element {
  const [notice, setNotice] = useState<NoticeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewAnnModal, setShowNewAnnModal] = useState(false);
  const [contractFile, setContractFile] = useState<AcceptanceDocData | null>(
    null
  );

  const [announcements, setAnnouncements] = useState<AnnouncementData[]>([]);
  const [pageAnnouncement, setPageAnnouncement] = useState(1);
  const [announcementsTotalPages, setAnnouncementsTotalPages] = useState(0);
  const announcementsPerPage = 10;

  const [workOrderNotice, setWorkOrderNotice] = useState<
    NoticesWorkOrdersData[]
  >([]);
  const [pageWoNotice, setPageWoNotice] = useState(1);
  const [woNoticeTotalPages, setWoNoticeTotalPages] = useState(0);
  const woNoticePerPage = 10;

  const [expanded, setExpanded] = useState(false);
  const showToggle = notice && notice.observations.length > 200;

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const navigate = useNavigate();
  const { notice: noticeIdParam } = useParams();
  const noticeId = parseInt(noticeIdParam as string, 10);
  const { getErrorMessage } = useErrorMessage();

  useEffect(() => {
    const fetchNoticeDetails = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const response = await getNotice(noticeId);
        if (response && response.data) {
          setNotice(response.data);
          const contractData: AcceptanceDocData = {
            id: 0,
            notice_id: noticeId,
            name: 'Contrato de prestação de serviço',
            download_url: response.data.contract_download_url || '',
            file_size: response.data.contract_file_size || 0,
            original_filename: response.data.contract_original_filename || '',
          };
          setContractFile(contractData);
        }
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNoticeDetails();
  }, [noticeId]);

  const handlePresentationVideoSaved = useCallback(
    (videoUrl: string | null): void => {
      setNotice((current) =>
        current ? { ...current, presentation_video_url: videoUrl } : current
      );
    },
    []
  );

  const { coverageData, getCoverage } = useFetchCoverageOperations({
    noticeId,
  });
  const {
    criteriasGroup,
    fetchTypologyGroups,
    isLoading: loadingCriteriasGroup,
  } = useTypologyGroups(noticeId);
  const {
    acceptanceDocs,
    fetchAcceptanceDocuments,
    isLoading: loadingAcceptanceDocs,
  } = useAcceptanceDocs(noticeId);

  const allAccreditationDocs = useMemo(
    () => [
      ...(contractFile && contractFile.download_url !== ''
        ? [contractFile]
        : []),
      ...acceptanceDocs,
    ],
    [contractFile, acceptanceDocs]
  );

  useEffect(() => {
    getCoverage();
    fetchTypologyGroups();
    fetchAcceptanceDocuments();
  }, []);

  const navigateToEdit = (): void => navigate('./edit');

  const fetchAnnouncements = async (): Promise<void> => {
    try {
      const response = await listAnnouncements(
        noticeId,
        pageAnnouncement,
        announcementsPerPage
      );

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      setAnnouncements(response.data);
      setAnnouncementsTotalPages(response.detail.total_pages || 0);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    if (noticeId) {
      fetchAnnouncements();
    }
  }, [noticeId, pageAnnouncement]);

  const handleWorkOrderNotice = async (): Promise<void> => {
    try {
      const response = await getNoticesWorkOrders(
        noticeId,
        pageWoNotice,
        woNoticePerPage
      );

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      setWorkOrderNotice(response.data);
      setWoNoticeTotalPages(response.detail.total_pages || 0);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    if (noticeId) {
      handleWorkOrderNotice();
    }
  }, [noticeId, pageWoNotice]);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <>
      <NoticeContainer>
        <NoticeVisualizationLabel container gap={2}>
          <Grid item>{IconVisibilityMS}</Grid>
          <Grid item>
            <Typography sx={{ fontWeight: 600 }}>
              {Constants.visibleToProto}
            </Typography>
            <Typography>{Constants.activateVisibility}</Typography>
          </Grid>
          <Grid
            item
            flex={1}
            display="flex"
            justifyContent="end"
            alignSelf="center"
          >
            <IOSSwitch checked={notice?.visible_to_proto} disabled />
          </Grid>
        </NoticeVisualizationLabel>
        <Grid container spacing={3}>
          <Grid item>
            <NoticeTitle>{notice?.name}</NoticeTitle>
          </Grid>
          <Grid item>
            <NoticeAttachments noticeId={noticeId} />
          </Grid>
          <Grid item flex={1} display="flex" justifyContent="end">
            <OutlinedButton onClick={navigateToEdit}>editar</OutlinedButton>
          </Grid>
        </Grid>
        <Grid container spacing={3} mt="12px">
          <Grid item>
            <InputTitle>Vigência do edital (início):</InputTitle>
            <StyledInput>
              {notice?.start_date ? formatDate(notice.start_date) : ''}
            </StyledInput>
          </Grid>
          <Grid item>
            <InputTitle>Vigência do edital (final):</InputTitle>
            <StyledInput>
              {notice?.end_date ? formatDate(notice.end_date) : ''}
            </StyledInput>
          </Grid>
          <Grid item xs={12}>
            <InputTitle>Observação:</InputTitle>
            <Box fontWeight={600} fontSize={18}>
              {showToggle ? (
                <>
                  {expanded
                    ? notice?.observations
                    : notice?.observations.slice(0, 200).concat('...')}
                  <MoreTypography onClick={() => setExpanded((prev) => !prev)}>
                    {expanded ? 'Mostrar menos' : 'Mostrar mais'}
                  </MoreTypography>
                </>
              ) : (
                notice?.observations
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <InputTitle>{Constants.licenseTypesAccepted}: </InputTitle>
            <StyledInput>
              {notice?.allowed_license_types
                ?.map((licenseType) => convertLicenseType(licenseType))
                .join(', ')}
            </StyledInput>
          </Grid>
        </Grid>
        <Grid item>
          <Stack direction="row" alignItems="center" gap={1} mt={2}>
            <Typography>{Constants.openToAccreditation}</Typography>
            <IOSSwitch checked={notice?.open_to_proto_accreditation} disabled />
          </Stack>
        </Grid>
      </NoticeContainer>
      {notice && <DocumentsModal noticeId={noticeId} noticeData={notice} />}
      <LinksModal noticeId={noticeId} />
      {notice && (
        <PresentationVideoSection
          noticeId={noticeId}
          initialUrl={notice.presentation_video_url ?? null}
          onSaved={handlePresentationVideoSaved}
        />
      )}
      <CoverageAreaSection data={coverageData} fetchCoverage={getCoverage} />
      <TypologyGroupSection
        isLoading={loadingCriteriasGroup}
        criteriasGroup={criteriasGroup}
        fetchTypologyGroups={fetchTypologyGroups}
      />
      <DocsSection
        data={allAccreditationDocs}
        isLoading={loadingAcceptanceDocs || isLoading}
      />
      <TableSection>
        <Box display="flex" justifyContent="flex-end" mb={3}>
          <Button onClick={() => setShowNewAnnModal(true)}>
            gerenciar comunicados
          </Button>
        </Box>
        <AnnouncementTable
          data={announcements}
          page={pageAnnouncement}
          totalPages={announcementsTotalPages}
          setPage={setPageAnnouncement}
          onAnnouncementUpdated={fetchAnnouncements}
        />
      </TableSection>
      <TableSection>
        <WorkOrderTable
          data={workOrderNotice}
          page={pageWoNotice}
          setPage={setPageWoNotice}
          totalPages={woNoticeTotalPages}
        />
      </TableSection>
      <NewAnnouncementModal
        isModalOpen={showNewAnnModal}
        onCloseModal={() => setShowNewAnnModal(false)}
        onAnnouncementUpdated={fetchAnnouncements}
        setAnnouncements={setAnnouncements}
        announcements={announcements}
        noticeId={noticeId}
      />
    </>
  );
}
